import type {
  DoctorService,
  DoctorServiceExpanded,
  ServiceDetails,
} from "@hermes/api";
import { Temporal, useMessage } from "@hermes/intl";
import { toast, useLocalStorage } from "@hermes/shared";
import { useSuspense } from "@hermes/utils";
import { useIsFetching, useIsMutating } from "@tanstack/react-query";
import { useEffect, useState } from "react";

export const sortServicesByName = (
  services?: DoctorServiceExpanded[],
): DoctorServiceExpanded[] | undefined => {
  return services?.toSorted((a, b) =>
    a.service.name.toLowerCase().localeCompare(b.service.name.toLowerCase()),
  );
};

export const useStoredLocale = () => {
  return useLocalStorage<string | undefined>("locale");
};

declare global {
  interface Window {
    prerenderReady?: boolean;
  }
}

export function usePrerenderReady(delay = 200) {
  const isFetching = useIsFetching();
  const isMutating = useIsMutating();

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | null = null;

    const checkReady = () => {
      if (isFetching === 0 && isMutating === 0) {
        // Small delay to ensure Helmet and Emotion finish
        timeout = setTimeout(() => {
          window.prerenderReady = true;
        }, delay);
      } else {
        // Still loading – reset if needed
        if (timeout) {
          clearTimeout(timeout);
          timeout = null;
        }
      }
    };

    checkReady(); // initial check

    const interval = setInterval(checkReady, 50); // re-check every 50ms

    return () => {
      clearInterval(interval);
      if (timeout) clearTimeout(timeout);
    };
  }, [isFetching, isMutating, delay]);
}

const sortDoctorServiceByNearestSlot = <
  DoctorServiceLike extends Pick<DoctorService, "nearest_slot_datetime">,
>(
  a: DoctorServiceLike,
  b: DoctorServiceLike,
) => {
  // Leave at the same position, services without slots will be filtered out later
  if (!a.nearest_slot_datetime || !b.nearest_slot_datetime) {
    return 0;
  }

  return Temporal.PlainDateTime.compare(
    a.nearest_slot_datetime,
    b.nearest_slot_datetime,
  );
};

export const sortDoctorServiceByFeedback = <
  DoctorServiceLike extends Pick<
    DoctorService,
    "feedback_count" | "feedback_score" | "nearest_slot_datetime"
  >,
>(
  a: DoctorServiceLike,
  b: DoctorServiceLike,
) => {
  if (b.feedback_count !== a.feedback_count) {
    return b.feedback_count - a.feedback_count;
  }
  return sortDoctorServiceByNearestSlot(a, b);
};

//Sort Doctor Service by service type: specialties and procedures, age: adult and child, name
export const sortDoctorServicesByType = ({
  services,
  selectedBranchId,
  includeProcedures = false,
}: {
  services: DoctorServiceExpanded[];
  selectedBranchId?: number;
  includeProcedures?: boolean;
}): DoctorServiceExpanded[] => {
  const filterAndSort = (type: string) => {
    return services
      .filter((s) => {
        return (
          (selectedBranchId === undefined ||
            s.clinic_branch === selectedBranchId) &&
          s.service.type === type
        );
      })
      .slice()
      .sort((a: any, b: any) => a.service.name.localeCompare(b.service.name))
      .sort(
        (a, b) =>
          Number(Boolean(a.service.adult_service)) -
          Number(Boolean(b.service.adult_service)),
      );
  };

  const specialties = filterAndSort("initial-appointment");
  const procedures = filterAndSort("procedure");

  return includeProcedures ? specialties.concat(procedures) : specialties;
};

//Sort doctor specialties by age, name
export const sortDoctorSpecialtiesByType = (services: ServiceDetails[]) => {
  const filterAndSort = (type: string) => {
    return services
      .filter((s) => s.type === type)
      .slice()
      .sort((a: any, b: any) => a.name.localeCompare(b.name))
      .sort(
        (a, b) =>
          Number(Boolean(a.adult_service)) - Number(Boolean(b.adult_service)),
      );
  };
  return filterAndSort("initial-appointment");
};

//Sort doctor for booking services
export const sortDoctorBookingSpecialties = (
  services: Pick<
    DoctorService,
    "service" | "service_name" | "service_type" | "age_to"
  >[],
) => {
  const filterAndSort = (type: string) => {
    const seen = new Set<string | undefined>();

    const isAdult = (s: { age_to: number | null }) =>
      s.age_to === 0 || s.age_to === null;

    const uniqueServices = services
      .filter((s) => s.service_type === type)
      .filter((s) => {
        if (seen.has(s.service_name)) return false;
        seen.add(s.service_name);
        return true;
      });

    return uniqueServices.sort((a, b) => {
      const isAdultA = isAdult(a);
      const isAdultB = isAdult(b);

      if (isAdultA !== isAdultB) {
        return isAdultA ? -1 : 1;
      }

      return (a.service_name || "").localeCompare(b.service_name || "");
    });
  };
  return filterAndSort("initial-appointment");
};
const SPECIALTY_THRESHOLD = 3;

/**
 * Selects the most relevant doctor service.
 *
 * If the total number of feedbacks is less or equal than 3:
 * 1. Consider only specialties with available slots
 * 2. Among them, prefer those with the earliest available slot
 * 3. Fallback to the service with the earliest available slot
 * 3. Fallback to first available specialty
 * 4. Fallback to first available service
 *
 * If the total number of feedbacks is more than 3:
 * 1. Consider only services with available slots
 * 2. Among them, prefer those with the highest number of feedbacks
 * 3. Fallback to service with the highest number of feedbacks
 * 4. Fallback to fist available service
 */
export const selectMainDoctorService = <
  DoctorServiceLike extends Pick<
    DoctorService | DoctorServiceExpanded,
    | "feedback_count"
    | "feedback_score"
    | "nearest_slot_datetime"
    | "clinic_branch"
    | "is_premium"
    | "service"
  > &
    Pick<DoctorService, "service_type">,
>(
  doctorServices: DoctorServiceLike[],
  filters?: {
    clinic_branch?: number;
    is_premium?: boolean;
    service?: number;
  },
): DoctorServiceLike | undefined => {
  // Applying filters means, that the function might return undefined
  // since these filters are meant to be strict
  const applyFilters = (dService: DoctorServiceLike) => {
    const serviceId =
      typeof dService.service === "number"
        ? dService.service
        : dService.service.id;
    return (
      (filters?.clinic_branch
        ? dService.clinic_branch === filters.clinic_branch
        : true) &&
      (typeof filters?.is_premium !== "undefined"
        ? filters.is_premium === dService.is_premium
        : true) &&
      (typeof filters?.service !== "undefined"
        ? filters.service === serviceId
        : true)
    );
  };

  const totalFeedbacks = doctorServices.reduce(
    (acc, dService) => acc + dService.feedback_count,
    0,
  );

  // If the total number of feedbacks is less or equal than 3
  if (totalFeedbacks <= SPECIALTY_THRESHOLD) {
    const filterBySpecialty = (dService: DoctorServiceLike) =>
      typeof dService.service_type === "undefined" &&
      typeof dService.service === "object"
        ? dService.service.type === "initial-appointment"
        : dService.service_type === "initial-appointment";

    const servicesWithSlots = doctorServices
      .filter((dService) => dService.nearest_slot_datetime)
      .sort(sortDoctorServiceByNearestSlot);
    const specialties = servicesWithSlots.filter(filterBySpecialty);

    return (
      // Consider specialty with the earliest available slot
      specialties.filter(applyFilters)[0] ??
      // Fallback to the service with the earliest available slot
      servicesWithSlots.filter(applyFilters)[0] ??
      // Fallback to first available specialty
      doctorServices.filter(filterBySpecialty).filter(applyFilters)[0] ??
      // Fallback to first available service
      doctorServices.filter(applyFilters)[0]
    );
  }

  // If the total number of feedbacks is more than 3
  const sortedByFeedback = doctorServices.sort(sortDoctorServiceByFeedback);
  const filteredBySlots = sortedByFeedback.filter(
    (service) => service.nearest_slot_datetime,
  );

  return (
    // Consider service with the highest number of feedbacks and available slots
    filteredBySlots.filter(applyFilters)[0] ??
    // Fallback to service with the highest number of feedbacks
    sortedByFeedback.filter(applyFilters)[0] ??
    // Fallback to fist available service
    doctorServices.filter(applyFilters)[0]
  );
};

export type GeoPosition = {
  lat: number;
  lng: number;
};

const getCurrentPositionAsync = () => {
  return new Promise<
    | { status: true; data: GeoPosition }
    | { status: false; error: GeolocationPositionError }
  >((resolve) => {
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        resolve({
          status: true,
          data: {
            lat: coords.latitude,
            lng: coords.longitude,
          },
        });
      },
      (err) => {
        resolve({
          status: false,
          error: err,
        });
      },
    );
  });
};

export const useGeolocation = ({
  enabled,
  onError,
  watch,
  suspense = true,
}: {
  enabled?: boolean;
  suspense?: boolean;
  onError?: () => void;
  watch?: boolean;
}) => {
  const message = useMessage();
  const [watchedPosition, setWatchedPosition] = useState<
    GeoPosition | undefined
  >();
  const [watchError, setWatchError] = useState<
    GeolocationPositionError | undefined
  >();
  const data = useSuspense(
    "position",
    () => getCurrentPositionAsync(),
    enabled && suspense,
  );

  useEffect(() => {
    if (!enabled || suspense) return;

    getCurrentPositionAsync().then((data) => {
      setWatchedPosition(data.status === true ? data.data : undefined);
      setWatchError(data.status === false ? data.error : undefined);
    });
  }, [enabled, suspense]);

  useEffect(() => {
    if (!enabled || !watch) return;

    const id = navigator.geolocation.watchPosition(
      ({ coords }) => {
        setWatchedPosition({
          lat: coords.latitude,
          lng: coords.longitude,
        });
        setWatchError(undefined);
      },
      (err) => {
        setWatchError(err);
        setWatchedPosition(undefined);
      },
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 10000,
      },
    );

    return () => {
      navigator.geolocation.clearWatch(id);
    };
  }, [enabled, watch]);

  const position = data?.status === true ? data.data : undefined;
  const error = watchError && (data?.status === false ? data.error : undefined);
  useEffect(() => {
    if (!error) return;
    onError?.();
    toast.warn(
      message({
        id: "cde077f9-d579-44d4-a1e9-1fefaaf66655",
        default:
          "Не можем определить ваше местоположение. Пожалуйста, дайте доступ к вашей геолокации",
      }),
    );
  }, [error?.code, message]);

  return {
    position: watchedPosition ?? position,
    error,
  };
};
